<template lang="pug">
div.commentary-type-chip
  VChip(
    :color="dark ? 'secondary-c500' : 'secondary-c900'"
    variant="outlined"
    @click="sheetOpen = true"
  )
    CommentaryTypeIcon.mr-1(
      :machine-name="props.commentaryTypeMachineName"
      variant="outline"
    )
    | {{ selectedCommentaryTypeName }}

  VBottomSheet(
    v-model="sheetOpen"
    style="max-width: 700px; margin-left: auto; margin-right: auto"
  )
    VList(:selected="selected")
      VListSubheader.text-h3 {{ i18n.t("Commentary.CommentaryType") }}
      VListItem(
        v-for="item in listItems"
        :key="item.uuid"
        :value="item.machineName"
        :disabled="item.count === 0"
        @click="model = item.machineName; sheetOpen = false"
      )
        template(#prepend)
          CommentaryTypeIcon(
            :machine-name="item.machineName"
            variant="outline"
          )
        VListItemTitle {{ item.name[locale] }}
        VListItemSubtitle
          span(
            v-html="i18n.t('Commentary.comments', { count: item.countHtml }, item.count)"
          )
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { usePreferencesStore } from "~/stores/usePreferencesStore";

import {
  getCommentaryTypeOrFallback,
  knownCommentaryTypes,
} from "~/static/taxonomy/commentaryTypes";
import { getCountData } from "./CommentaryType.util";
import { useDarkMode } from "~/composables/useDarkMode";

interface Props {
  /**
   * The machine name of the selected commentary type.
   */
  commentaryTypeMachineName: string;

  /**
   * A nested map of commentary type counts, first keyed by language, then by commentary type machine names.
   * The values are the number of available comments for each type.
   */
  counts: Map<string, Map<string, number>>;
}

const i18n = useI18n();
const locale = i18n.locale;
const dark = useDarkMode();
const preferencesStore = usePreferencesStore();

const otherLanguages = computed<string[]>(() =>
  preferencesStore.getOtherLanguages(unref(i18n.locale)),
);

const props = withDefaults(defineProps<Props>(), {});

const sheetOpen = ref(false);

const selected = computed(() => [props.commentaryTypeMachineName]);

const emit = defineEmits<{
  "update:commentaryTypeMachineName": [value: string];
}>();

const model = computed<string>({
  get() {
    return props.commentaryTypeMachineName;
  },
  set(value) {
    emit("update:commentaryTypeMachineName", value);
  },
});

const listItems = computed(() => {
  return knownCommentaryTypes.map((type) => {
    const { countHtml, countTotal } = getCountData({
      machineName: type.machineName,
      counts: props.counts,
      locale: locale.value,
      otherLanguages: unref(otherLanguages),
    });
    return {
      name: type.name,
      machineName: type.machineName,
      uuid: type.uuid,
      countHtml,
      count: countTotal,
    };
  });
});

const selectedCommentaryTypeName = computed<string>((): string => {
  return getCommentaryTypeOrFallback(model.value).name[locale.value] ?? "";
});
</script>
